/**
 * Reconciliation.js — Reconciliação de tickets sem saída (Onda 16)
 * CI Sistemas de Informática Ltda. — FastALPR Parking
 *
 * Lista os tickets abertos (ativos, ou finalizados pela tarefa de abandono sem
 * saída) que têm um par provável: uma leitura de saída com placa variante de
 * confusão do OCR, ou um ticket gêmeo criado segundos depois na mesma câmera.
 * O operador decide caso a caso: fechar com a saída (cobrando a permanência
 * real), fundir os gêmeos ou manter. Nada é automático.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import '../styles/Reconciliation.css';

const KIND_LABEL = {
  fechar_com_saida: 'Fechar com saída variante',
  entrada_repetida: 'Entrada repetida',
  fundir_com_ticket: 'Tickets gêmeos',
  ambiguo: 'Ambíguos',
  saida_perdida: 'Saída nunca vista',
  // Onda 77: não é um problema, é o estado normal de quem estacionou. Aparece
  // como número para o gestor reconhecer o que antes era contado como anomalia.
  no_patio: 'No pátio agora',
};

function fmtDateTime(dt) { return dt ? new Date(dt).toLocaleString('pt-BR') : '—'; }
function fmtDuration(from, to) {
  const min = Math.max(0, Math.round((new Date(to) - new Date(from)) / 60000));
  const h = Math.floor(min / 60); const m = min % 60;
  return h ? `${h} h ${String(m).padStart(2, '0')} min` : `${m} min`;
}
function evidenceUrl(p, token) { return p ? `/api/evidence/${p}?token=${encodeURIComponent(token || '')}` : null; }
function engineUrl(cropPath, token) {
  if (!cropPath) return null;
  const jpg = cropPath.replace(/\.crop\.png$/, '.jpg');
  return `/api/ocr-quality/file/${jpg}?token=${encodeURIComponent(token || '')}`;
}

// Placa com os caracteres diferentes da outra placa em destaque
function PlateDiff({ plate, other }) {
  return (
    <span className="rec-plate">
      {(plate || '').split('').map((ch, i) => (
        <span key={i} className={`rec-char ${other && other[i] !== ch ? 'diff' : ''}`}>{ch}</span>
      ))}
    </span>
  );
}

function StatusBadge({ ticket }) {
  const abandoned = ticket.status === 'finalizado';
  return (
    <span className={`rec-badge ${abandoned ? 'rec-badge-abandoned' : 'rec-badge-active'}`}
      title={abandoned ? 'Finalizado pela tarefa de abandono (24 h sem saída), sem cobrança' : 'Ticket ativo'}>
      {abandoned ? 'abandonado' : 'ativo'}
    </span>
  );
}

// A miniatura abre o modal desta tela, não uma aba nova: o operador precisa ver
// a foto grande e decidir ali mesmo, sem perder o contexto do caso.
function Evidence({ url, label, onOpen }) {
  const [failed, setFailed] = useState(false);
  if (!url || failed) return <div className="rec-evidence rec-evidence-empty">{label}: sem imagem</div>;
  return (
    <button type="button" className="rec-evidence" onClick={() => onOpen && onOpen({ url, label })} title={`${label} — ampliar`}>
      <img src={url} alt={label} onError={() => setFailed(true)} />
      <span>{label}</span>
    </button>
  );
}

// ─── Modal da evidência, com as ações do caso ao lado da foto ───────────────
function EvidenceModal({ view, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (!view) return null;
  const { url, label, actions, caption } = view;
  return (
    <div className="rec-modal-backdrop" onClick={onClose} role="presentation">
      <div className="rec-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={label}>
        <header className="rec-modal-head">
          <h3>{label}</h3>
          <button type="button" className="rec-modal-close" onClick={onClose} aria-label="Fechar">×</button>
        </header>
        <div className="rec-modal-body">
          <img src={url} alt={label} />
          {caption && <p className="rec-modal-caption">{caption}</p>}
        </div>
        <footer className="rec-modal-foot">
          {(actions || []).map((a, i) => (
            <button key={i} type="button" className={`btn btn-sm ${a.variant || 'btn-secondary'}`}
              disabled={a.disabled} onClick={() => { onClose(); a.run(); }}>{a.label}</button>
          ))}
          <button type="button" className="btn btn-sm btn-secondary" onClick={onClose}>Fechar</button>
        </footer>
      </div>
    </div>
  );
}

// ─── Onda 37: placa com ticket em aberto vista de novo pela câmera de entrada ──
// A câmera de entrada aponta para a avenida. Uma placa com ticket em aberto lida
// por ela mostra o veículo fora do pátio, ou chegando outra vez: em qualquer dos
// casos ele saiu em algum momento sem a câmera de saída registrar. O que NÃO
// existe aqui é evidência do instante da saída, e a tela não pode fingir que
// existe — antes ela rotulava o caso como "saída sem leitura" mostrando apenas a
// foto da câmera de entrada.
function TailgateCase({ caso, token, onOpen, canAct, busy, onCloseAtSighting, onKeep, onEndUnpaid }) {
  const { reading, companion, kind, hours_since_entry: horas } = caso;
  const img = evidenceUrl(reading.image_path, token) || engineUrl(reading.plate_crop_path, token);
  const entradaImg = evidenceUrl(reading.entry_image_path, token);
  const companionImg = companion ? evidenceUrl(companion.image_path, token) : null;
  const saidasDepois = Number(reading.exits_after_entry) || 0;
  return (
    <article className="rec-case">
      <div className="rec-case-head">
        <div>
          <div className="rec-case-title">
            <span className="rec-plate">{reading.plate}</span>
            <span className={`rec-badge ${kind === 'carona' ? 'rec-badge-abandoned' : 'rec-badge-active'}`}>
              {kind === 'carona' ? 'outro veículo saiu no mesmo instante' : 'sem evidência de saída'}
            </span>
          </div>
          <div className="rec-muted">
            Ticket #{reading.ticket_id} aberto em {fmtDateTime(reading.entry_time)}. A placa foi lida de novo pela
            {' '}{reading.camera_name || `câmera ${reading.camera_id}`} em {fmtDateTime(reading.detected_at)},
            {' '}{horas} h depois.
            {reading.unused_reason === 'entrada_duplicada' ? ' A abertura de um segundo ticket foi barrada.' : ''}
          </div>
          <div className="rec-muted">
            {saidasDepois > 0
              ? `A câmera de saída registrou esta placa ${saidasDepois} vez(es) depois desta entrada, em outro ticket.`
              : 'A câmera de saída nunca registrou esta placa depois desta entrada: não há foto de saída para mostrar.'}
          </div>
          {companion && (
            <div className="rec-muted">
              {companion.plate} foi registrado saindo {Math.abs(companion.delta_seconds)} s
              {companion.delta_seconds < 0 ? ' antes' : ' depois'} desta leitura, pela {companion.camera_name || `câmera ${companion.camera_id}`}.
              É o indício de que os dois passaram juntos.
            </div>
          )}
        </div>
      </div>
      <div className="rec-evidences">
        <Evidence url={entradaImg} label={`Entrada do ticket #${reading.ticket_id}`} onOpen={onOpen} />
        <Evidence url={img} label={`${reading.plate} visto de novo na entrada`} onOpen={onOpen} />
        {companionImg && <Evidence url={companionImg} label={`${companion.plate} saindo`} onOpen={onOpen} />}
      </div>
      {canAct && reading.ticket_id && (
        <div className="rec-actions">
          <button className="btn btn-primary" disabled={busy}
            onClick={() => onEndUnpaid({ id: reading.ticket_id, plate: reading.plate, entry_time: reading.entry_time }, reading.id)}>
            Encerrar #{reading.ticket_id} sem cobrança e registrar na Quarentena
          </button>
          {companion && (
            <button className="btn btn-secondary" disabled={busy} onClick={() => onCloseAtSighting(caso)}>
              Cobrar até {fmtDateTime(reading.detected_at)}
            </button>
          )}
          <button className="btn btn-secondary" disabled={busy} onClick={() => onKeep({ id: reading.ticket_id })}>
            Revisado, manter aberto
          </button>
        </div>
      )}
      <p className="rec-help">
        Sem horário de saída comprovado, <strong>encerrar sem cobrança</strong> é o desfecho honesto: o veículo saiu,
        mas ninguém mediu quando. Cobrar até o instante da leitura só aparece quando outro veículo foi registrado
        saindo nos mesmos segundos, porque aí o horário é plausível.
      </p>
    </article>
  );
}

// As mesmas ações do card acompanham a foto no modal, para o operador decidir
// olhando a imagem grande em vez de voltar ao card.
function abrirCom(onOpen, actions, caption) {
  return (view) => onOpen && onOpen({ ...view, actions, caption });
}

function acoesFechar(item, canAct, busy, onClose, onKeep) {
  if (!canAct) return [];
  return [
    { label: 'Fechar com esta saída', variant: 'btn-primary', disabled: busy, run: () => onClose(item) },
    { label: 'Não é o mesmo carro, manter', disabled: busy, run: () => onKeep(item.ticket) },
  ];
}

// ─── Caso: fechar com saída variante ────────────────────────────────────────
function CloseCase({ item, token, canAct, busy, onClose, onKeep, onOpen }) {
  const { ticket, exit } = item;
  const exitImg = evidenceUrl(exit.image_path, token) || engineUrl(exit.plate_crop_path, token);
  return (
    <article className="rec-case">
      <div className="rec-case-head">
        <div>
          <div className="rec-case-title">
            Ticket #{ticket.id} <PlateDiff plate={ticket.plate} other={exit.plate} /> <StatusBadge ticket={ticket} />
          </div>
          <div className="rec-muted">Entrada {fmtDateTime(ticket.entry_time)} · {ticket.entry_camera_name || `câmera ${ticket.entry_camera_id}`}</div>
        </div>
        <div className="rec-arrow">→</div>
        <div>
          <div className="rec-case-title">
            Saída lida como <PlateDiff plate={exit.plate} other={ticket.plate} />
            <span className="rec-dist" title="Distância ponderada pela confusão do OCR (0,35 por par confundível)">d = {exit.distance.toFixed(2)}</span>
          </div>
          <div className="rec-muted">{fmtDateTime(exit.detected_at)} · {exit.camera_name || `câmera ${exit.camera_id}`} · permanência {fmtDuration(ticket.entry_time, exit.detected_at)}</div>
        </div>
      </div>
      <div className="rec-evidences">
        <Evidence url={evidenceUrl(ticket.entry_image_path, token)} label="Entrada" onOpen={abrirCom(onOpen, acoesFechar(item, canAct, busy, onClose, onKeep))} />
        <Evidence url={exitImg} label="Saída" onOpen={abrirCom(onOpen, acoesFechar(item, canAct, busy, onClose, onKeep))} />
      </div>
      {canAct && (
        <div className="rec-actions">
          <button className="btn btn-primary" disabled={busy} onClick={() => onClose(item)}>Fechar com esta saída</button>
          <button className="btn btn-secondary" disabled={busy} onClick={() => onKeep(ticket)}>Não é o mesmo carro, manter</button>
        </div>
      )}
    </article>
  );
}

// ─── Caso: entrada repetida (Onda 34) ───────────────────────────────────────
// A mesma placa com dois tickets em aberto. O carro não entrou duas vezes: a
// segunda leitura é ele manobrando ou saindo pela área da câmera de entrada,
// e a hora dela é, na prática, a hora da saída.
function RepeatedEntryCase({ item, token, canAct, busy, onCloseAtDuplicate, onMerge, onKeep, onOpen }) {
  const dup = item.ticket;        // o mais novo: o ticket que sobrou
  const original = item.twin;     // o mais antigo: a entrada verdadeira
  const acoes = !canAct ? [] : [
    {
      label: `Fechar #${original.id} às ${fmtDateTime(dup.entry_time)} e cancelar #${dup.id}`,
      variant: 'btn-primary', disabled: busy, run: () => onCloseAtDuplicate(original, dup),
    },
    { label: `Só cancelar #${dup.id}`, disabled: busy, run: () => onMerge(dup, original) },
    { label: 'Manter os dois', disabled: busy, run: () => onKeep(dup, original) },
  ];
  return (
    <article className="rec-case">
      <div className="rec-case-head">
        <div>
          <div className="rec-case-title">
            Ticket #{original.id} <span className="rec-plate">{original.plate}</span> <StatusBadge ticket={original} />
          </div>
          <div className="rec-muted">Entrada verdadeira em {fmtDateTime(original.entry_time)} · {original.entry_camera_name || `câmera ${original.entry_camera_id}`}</div>
        </div>
        <div className="rec-arrow">→</div>
        <div>
          <div className="rec-case-title">
            Ticket #{dup.id} <span className="rec-plate">{dup.plate}</span> <StatusBadge ticket={dup} />
          </div>
          <div className="rec-muted">
            Segunda leitura em {fmtDateTime(dup.entry_time)} · permanência de {fmtDuration(original.entry_time, dup.entry_time)}
          </div>
        </div>
      </div>
      <div className="rec-evidences">
        <Evidence url={evidenceUrl(original.entry_image_path, token)} label={`Entrada do #${original.id}`}
          onOpen={abrirCom(onOpen, acoes, 'Entrada verdadeira do veículo.')} />
        <Evidence url={evidenceUrl(dup.entry_image_path, token)} label={`Segunda leitura (#${dup.id})`}
          onOpen={abrirCom(onOpen, acoes, 'Se o veículo aparece de traseira ou se afastando, esta é a saída dele.')} />
      </div>
      {canAct && (
        <div className="rec-actions">
          {acoes.map((a, i) => (
            <button key={i} className={`btn ${a.variant || 'btn-secondary'}`} disabled={a.disabled} onClick={a.run}>{a.label}</button>
          ))}
        </div>
      )}
      <p className="rec-help">
        Fechar o ticket original com a hora da segunda leitura cobra a permanência real e cancela o duplicado de uma vez.
        Escolha "só cancelar" se o veículo apenas manobrou e continua no pátio.
      </p>
    </article>
  );
}

// ─── Caso: tickets gêmeos ───────────────────────────────────────────────────
function suggestKeep(a, b) {
  const ca = Number(a.entry_confidence || 0); const cb = Number(b.entry_confidence || 0);
  if (ca !== cb) return ca > cb ? a : b;
  return new Date(a.entry_time) <= new Date(b.entry_time) ? a : b;
}

function TwinCase({ a, b, token, canAct, busy, onMerge, onKeep, onOpen }) {
  const keep = suggestKeep(a, b); const drop = keep.id === a.id ? b : a;
  const Side = ({ t, other }) => (
    <div className={`rec-twin ${t.id === keep.id ? 'rec-twin-keep' : ''}`}>
      <div className="rec-case-title">Ticket #{t.id} <PlateDiff plate={t.plate} other={other.plate} /> <StatusBadge ticket={t} /></div>
      <div className="rec-muted">
        {fmtDateTime(t.entry_time)}{t.entry_confidence != null && <> · confiança {(Number(t.entry_confidence) * 100).toFixed(1)}%</>}
        {t.id === keep.id && <span className="rec-suggest"> · sugerido manter</span>}
      </div>
      <Evidence url={evidenceUrl(t.entry_image_path, token)} label="Entrada" />
    </div>
  );
  return (
    <article className="rec-case">
      <div className="rec-case-head rec-case-head-twin">
        <Side t={a} other={b} />
        <Side t={b} other={a} />
      </div>
      {canAct && (
        <div className="rec-actions">
          <button className="btn btn-primary" disabled={busy} onClick={() => onMerge(drop, keep)}>Manter #{keep.id} ({keep.plate}) e cancelar #{drop.id}</button>
          <button className="btn btn-secondary" disabled={busy} onClick={() => onMerge(keep, drop)}>Manter #{drop.id} ({drop.plate}) e cancelar #{keep.id}</button>
          <button className="btn btn-secondary" disabled={busy} onClick={() => onKeep(a, b)}>São carros diferentes, manter os dois</button>
        </div>
      )}
    </article>
  );
}

// ─── Página ──────────────────────────────────────────────────────────────────
export default function Reconciliation() {
  const { can } = useAuth();
  const token = localStorage.getItem('token');
  const canAct = can('reconciliation_act');
  const [days, setDays] = useState(14);
  const [includeLost, setIncludeLost] = useState(false);
  const [data, setData] = useState(null);
  const [tailgating, setTailgating] = useState(null);
  const [evidenceView, setEvidenceView] = useState(null);
  const [bulkOlderThan, setBulkOlderThan] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/reconciliation/candidates?days=${days}&include_lost=${includeLost}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao carregar candidatos');
    } finally { setLoading(false); }
  }, [days, includeLost]);

  // Onda 33: levantamento separado, porque não depende dos tickets sem saída.
  const loadTailgating = useCallback(async () => {
    try {
      const r = await api.get(`/api/reconciliation/tailgating?days=${days}`);
      setTailgating(r.data);
    } catch (_) { setTailgating(null); }
  }, [days]);
  useEffect(() => { loadTailgating(); }, [loadTailgating]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); await load(); await loadTailgating(); }
    catch (e) { toast.error(e.response?.data?.error || 'Falha na operação'); }
    finally { setBusy(false); }
  };
  const onClose = (item) => {
    const { ticket, exit } = item;
    if (!window.confirm(`Fechar o ticket #${ticket.id} (${ticket.plate}) com a saída ${exit.plate} de ${fmtDateTime(exit.detected_at)}?\nPermanência de ${fmtDuration(ticket.entry_time, exit.detected_at)} será cobrada pela tabela do ticket (baixa automática).`)) return;
    act(async () => {
      const r = await api.post(`/api/reconciliation/tickets/${ticket.id}/close`, { plate_log_id: exit.id });
      return r;
    }, `Ticket #${ticket.id} fechado`);
  };
  const onMerge = (drop, keep) => {
    if (!window.confirm(`Cancelar o ticket #${drop.id} (${drop.plate}) e manter o #${keep.id} (${keep.plate})?`)) return;
    act(() => api.post(`/api/reconciliation/tickets/${drop.id}/merge`, { into_ticket_id: keep.id }), `Ticket #${drop.id} fundido no #${keep.id}`);
  };
  const onKeep = (...tickets) => {
    act(async () => { for (const t of tickets) await api.post(`/api/reconciliation/tickets/${t.id}/keep`); }, 'Revisado e mantido');
  };
  // Onda 36: o veículo saiu em momento desconhecido; encerrar sem cobrança tira
  // o carro do pátio virtual sem arbitrar uma permanência que ninguém mediu.
  // Onda 38: quando o encerramento parte de um avistamento, a placa entra na
  // Quarentena, com o contador que revela o padrão de reincidência.
  const onEndUnpaid = (ticket, plateLogId = null) => {
    if (!window.confirm(
      `Encerrar o ticket #${ticket.id} (${ticket.plate}) sem cobrança?\n` +
      `A entrada foi em ${fmtDateTime(ticket.entry_time)} e a saída nunca foi lida. Nada será cobrado.` +
      (plateLogId ? '\nA placa será registrada na Quarentena para acompanhamento.' : '')
    )) return;
    act(async () => {
      const r = await api.post(`/api/reconciliation/tickets/${ticket.id}/end-unpaid`,
        plateLogId ? { plate_log_id: plateLogId } : {});
      if (r.data && r.data.quarentena) {
        toast.success(`${ticket.plate} registrado na Quarentena${r.data.quarentena.companion ? ` junto de ${r.data.quarentena.companion}` : ''}`);
      }
    }, `Ticket #${ticket.id} encerrado sem cobrança`);
  };

  const onBulkKeep = () => {
    const alvo = bulkOlderThan === 0 ? 'todos os casos listados' : `os que entraram há mais de ${bulkOlderThan} dia(s)`;
    if (!window.confirm(
      `Marcar como revisados ${alvo}?\n` +
      'Isso só tira os tickets desta lista: nenhum valor, status ou horário é alterado.'
    )) return;
    act(async () => {
      const r = await api.post('/api/reconciliation/bulk/keep', { days, older_than_days: bulkOlderThan });
      toast.success(`${r.data.marcados} ticket(s) marcados como revisados`);
    }, 'Revisão em lote registrada');
  };

  // Onda 36: fecha o ticket com o horário em que a zona de observação viu o
  // veículo saindo, cobrando a permanência real.
  const onCloseAtSighting = (caso) => {
    const { reading } = caso;
    if (!window.confirm(
      `Fechar o ticket #${reading.ticket_id} (${reading.plate}) com saída em ${fmtDateTime(reading.detected_at)}?\n` +
      'A permanência até esse horário será cobrada pela tabela do ticket.'
    )) return;
    act(() => api.post(`/api/reconciliation/tickets/${reading.ticket_id}/close-at-sighting`, { plate_log_id: reading.id }),
      `Ticket #${reading.ticket_id} fechado pelo avistamento`);
  };

  // Onda 34: fecha o original com a hora da segunda leitura e cancela o duplicado.
  const onCloseAtDuplicate = (original, dup) => {
    if (!window.confirm(
      `Fechar o ticket #${original.id} (${original.plate}) com saída em ${fmtDateTime(dup.entry_time)} e cancelar o #${dup.id}?\n` +
      `Permanência de ${fmtDuration(original.entry_time, dup.entry_time)} será cobrada pela tabela do ticket.`
    )) return;
    act(() => api.post(`/api/reconciliation/tickets/${original.id}/close-at-duplicate`, { duplicate_ticket_id: dup.id }),
      `Ticket #${original.id} fechado e #${dup.id} cancelado`);
  };

  const items = data?.items || [];
  const closeItems = items.filter(i => i.kind === 'fechar_com_saida');
  const repeatedItems = items.filter(i => i.kind === 'entrada_repetida');
  const twinPairs = []; const seen = new Set();
  for (const i of items.filter(x => x.kind === 'fundir_com_ticket')) {
    const key = [i.ticket.id, i.twin.id].sort((x, y) => x - y).join('-');
    if (seen.has(key)) continue; seen.add(key);
    twinPairs.push([i.ticket, i.twin]);
  }
  const ambiguous = items.filter(i => i.kind === 'ambiguo');
  const lost = items.filter(i => i.kind === 'saida_perdida');
  const counts = data?.counts || {};
  // Onda 77: o número vem do servidor, que o lê das configurações. A tela não
  // repete a regra — se o parâmetro mudar, o texto muda junto.
  const graceHoras = data?.grace_hours ?? 12;

  return (
    <div className="rec-page">
      <div className="page-header">
        <div>
          <h1>Reconciliação de tickets</h1>
          <p className="page-subtitle">Tickets sem saída com um par provável: saída lida com placa variante, ou ticket gêmeo. Cada decisão é sua e fica registrada na auditoria.</p>
          <p className="rec-help rec-help-topo">
            <strong>No pátio agora</strong> conta os veículos que entraram há pouco e ainda não saíram — o
            funcionamento normal do estacionamento, não um caso a resolver. Eles não aparecem nas listas
            abaixo. Um ticket só passa a "saída nunca vista" depois de {graceHoras} h no pátio.
          </p>
        </div>
        <div className="rec-controls">
          <div className="rec-days">
            {[7, 14, 30].map(d => <button key={d} className={`btn btn-sm ${days === d ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setDays(d)}>{d} dias</button>)}
          </div>
          <label><input type="checkbox" checked={includeLost} onChange={e => setIncludeLost(e.target.checked)} /> listar saídas nunca vistas</label>
        </div>
      </div>

      <div className="rec-cards">
        {['no_patio', 'fechar_com_saida', 'entrada_repetida', 'fundir_com_ticket', 'ambiguo', 'saida_perdida'].map(k => (
          <div key={k} className={`rec-card rec-card-${k}`}>
            <span className="rec-card-label">{KIND_LABEL[k]}</span>
            <span className="rec-card-value">{counts[k] || 0}</span>
          </div>
        ))}
      </div>

      {loading ? <div className="rec-empty">Carregando…</div> : (
        <>
          <section>
            <h2>{KIND_LABEL.fechar_com_saida} <span className="rec-count">{closeItems.length}</span></h2>
            <p className="rec-help">A saída foi lida com um ou dois caracteres confundíveis (B/D/8, A/4, O/0/Q…) e não fechou ticket próprio. Fechar cobra a permanência real e ensina a placa correta à tela de Qualidade do OCR.</p>
            {closeItems.length === 0 && <div className="rec-empty">Nenhum caso pendente.</div>}
            {closeItems.map(i => <CloseCase key={i.ticket.id} item={i} token={token} canAct={canAct} busy={busy} onClose={onClose} onKeep={onKeep} onOpen={setEvidenceView} />)}
          </section>

          <section>
            <h2>{KIND_LABEL.entrada_repetida} <span className="rec-count">{repeatedItems.length}</span></h2>
            <p className="rec-help">
              A mesma placa com dois tickets em aberto. O veículo não entrou duas vezes: a segunda leitura é ele
              manobrando ou saindo pela área da câmera de entrada, e a hora dela é a hora real da saída.
            </p>
            {repeatedItems.length === 0 && <div className="rec-empty">Nenhum caso pendente.</div>}
            {repeatedItems.map(i => (
              <RepeatedEntryCase key={i.ticket.id} item={i} token={token} canAct={canAct} busy={busy}
                onCloseAtDuplicate={onCloseAtDuplicate} onMerge={onMerge} onKeep={onKeep} onOpen={setEvidenceView} />
            ))}
          </section>

          <section>
            <h2>{KIND_LABEL.fundir_com_ticket} <span className="rec-count">{twinPairs.length}</span></h2>
            <p className="rec-help">Dois tickets criados com segundos de diferença na mesma câmera, com placas variantes: o mesmo carro lido duas vezes. Um deles é cancelado e aponta para o outro.</p>
            {twinPairs.length === 0 && <div className="rec-empty">Nenhum caso pendente.</div>}
            {twinPairs.map(([a, b]) => <TwinCase key={`${a.id}-${b.id}`} a={a} b={b} token={token} canAct={canAct} busy={busy} onMerge={onMerge} onKeep={onKeep} onOpen={setEvidenceView} />)}
          </section>

          {ambiguous.length > 0 && (
            <section>
              <h2>{KIND_LABEL.ambiguo} <span className="rec-count">{ambiguous.length}</span></h2>
              <p className="rec-help">Mais de um par à mesma distância. Verifique pelas evidências na tela de Tickets antes de decidir.</p>
              <ul className="rec-list">
                {ambiguous.map(i => (
                  <li key={i.ticket.id}>Ticket #{i.ticket.id} {i.ticket.plate} ({fmtDateTime(i.ticket.entry_time)}) — candidatos: {i.ambiguous.map(c => c.plate + (c.ticket_id ? ` (#${c.ticket_id})` : '')).join(', ')}</li>
                ))}
              </ul>
            </section>
          )}

          <section>
            <h2>Ticket em aberto com a placa vista de novo <span className="rec-count">{(tailgating?.items || []).length}</span></h2>
            <p className="rec-help">
              A câmera de entrada aponta para a avenida. Quando ela lê uma placa que ainda tem ticket em aberto, o
              veículo está fora do pátio, ou chegando outra vez: nos dois casos ele saiu em algum momento sem a
              câmera de saída registrar. <strong>Não há evidência do instante da saída</strong>, e por isso cada caso
              mostra a foto da entrada ao lado da leitura nova, sem foto de saída. Quando outro veículo aparece
              saindo nos mesmos segundos, existe indício de que os dois passaram juntos.
            </p>
            {(tailgating?.items || []).length === 0 && <div className="rec-empty">Nenhum caso no período.</div>}
            {(tailgating?.items || []).map(c => <TailgateCase key={c.reading.id} caso={c} token={token} onOpen={setEvidenceView} canAct={canAct} busy={busy} onCloseAtSighting={onCloseAtSighting} onKeep={onKeep} onEndUnpaid={onEndUnpaid} />)}
          </section>

          {includeLost && (
            <section>
              <h2>{KIND_LABEL.saida_perdida} <span className="rec-count">{lost.length}</span></h2>
              <p className="rec-help">
                Nenhuma leitura de saída parecida no período, e o ticket já passou das {graceHoras} h de pátio.
                Quase sempre o carro saiu enquanto a câmera de saída estava sem sinal ou com a zona antiga.
                Não há horário de saída a cobrar: arbitrar um inventaria
                permanência. <strong>Revisado</strong> tira o ticket desta lista sem mudar nada.
                <strong> Encerrar sem cobrança</strong> ainda fecha o ticket que continua ativo, para o veículo
                sair do pátio virtual. A ação em lote marca de uma vez os casos mais antigos.
              </p>
              {canAct && lost.length > 0 && (
                <div className="rec-actions rec-bulk">
                  <label>
                    Marcar como revisados os que entraram há mais de
                    <select value={bulkOlderThan} onChange={e => setBulkOlderThan(Number(e.target.value))} disabled={busy}>
                      {[0, 1, 3, 7].map(n => <option key={n} value={n}>{n === 0 ? 'qualquer tempo' : `${n} dia${n > 1 ? 's' : ''}`}</option>)}
                    </select>
                  </label>
                  <button className="btn btn-secondary" disabled={busy} onClick={onBulkKeep}>Marcar em lote</button>
                </div>
              )}
              <div className="rec-table-wrap">
                <table className="rec-table">
                  <thead><tr><th>Ticket</th><th>Placa</th><th>Entrada</th><th>Câmera</th><th>Status</th><th></th></tr></thead>
                  <tbody>
                    {lost.map(i => (
                      <tr key={i.ticket.id}>
                        <td>#{i.ticket.id}</td>
                        <td className="rec-mono">{i.ticket.plate}</td>
                        <td>{fmtDateTime(i.ticket.entry_time)}</td>
                        <td>{i.ticket.entry_camera_name || i.ticket.entry_camera_id}</td>
                        <td><StatusBadge ticket={i.ticket} /></td>
                        <td className="rec-row-actions">
                          {canAct && (
                            <>
                              <button className="btn btn-sm btn-secondary" disabled={busy} onClick={() => onKeep(i.ticket)}>Revisado</button>
                              {i.ticket.status === 'ativo' && (
                                <button className="btn btn-sm btn-secondary" disabled={busy} onClick={() => onEndUnpaid(i.ticket)}>Encerrar sem cobrança</button>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
      <EvidenceModal view={evidenceView} onClose={() => setEvidenceView(null)} />
    </div>
  );
}
